import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ConfigProvider, Result } from 'antd'
import zhCN from 'antd/locale/zh_CN'
import PrivateRoute from './components/PrivateRoute'
import AdminLayout from './components/AdminLayout'
import LoginPage from './pages/Login'
import DashboardPage from './pages/Dashboard'
import LeadsPage from './pages/Leads'
import RewardsPage from './pages/Rewards'
import PaymentsPage from './pages/Payments'
import BlacklistPage from './pages/Blacklist'
import ReviewCenterPage from './pages/ReviewCenter'
import ReferrersPage from './pages/Referrers'
import RiskControlPage from './pages/RiskControl'
import SystemConfigPage from './pages/SystemConfig'
import ReportsPage from './pages/Reports'
import ProjectIntroPage from './pages/ProjectIntro'
// thankyou_v2b：ReferredBenefitsPage 不再直接挂载（/referred-benefits → /benefit-config 重定向），
// 页面文件已于 thankyou_v2 收尾删除，仅移除未使用 import 以过 noUnusedLocals。
import BenefitConfigPage from './pages/BenefitConfig'
import MerchantsPage from './pages/Merchants'
import { useAuthStore, hasAnyRole, AdminRole } from './stores/auth'

// 角色守卫：命中黑名单角色时渲染 403（不跳转，保留侧边栏导航）
const RoleRoute: React.FC<{ roles: AdminRole[]; children: React.ReactNode }> = ({ roles, children }) => {
  const role = useAuthStore((s) => s.user?.role)
  if (!hasAnyRole(role, ...roles)) {
    return (
      <Result
        status="403"
        title="无访问权限"
        subTitle="当前角色无权访问该页面，请联系管理员。"
      />
    )
  }
  return <>{children}</>
}

// 移动端兼容：触控目标 ≥44px；仅触屏/窄视口生效，桌面端不受影响。
// 全程未使用裸 :hover（如需悬停样式须包在 @media (hover: hover) 内）。
const MOBILE_COMPAT_CSS = `
@media (hover: none), (max-width: 768px) {
  .ant-btn { min-height: 44px; }
  .ant-btn-sm { min-height: 44px; font-size: 13px; }
  .ant-tabs-tab { min-height: 44px; }
  .ant-menu-item { min-height: 44px; }
  .ant-pagination .ant-pagination-item,
  .ant-pagination .ant-pagination-prev,
  .ant-pagination .ant-pagination-next { min-width: 44px; min-height: 44px; line-height: 42px; }
  .ant-table-tbody > tr > td { padding-top: 10px; padding-bottom: 10px; }
}
`

const App: React.FC = () => {
  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        token: {
          colorPrimary: '#1A873B',
          colorInfo: '#1A873B',
          colorWarning: '#F39C12',
          colorSuccess: '#27AE60',
          colorError: '#E74C3C',
          colorBgLayout: '#F5F7FA',
          borderRadius: 8,
        },
        components: {
          Menu: {
            // light 主题菜单令牌：选中/悬停统一为品牌绿（修复展开子菜单仍显示默认蓝色）
            itemColor: '#333333',
            itemHoverColor: '#1A873B',
            itemSelectedColor: '#1A873B',
            itemSelectedBg: '#EAF5ED',
            subMenuItemBg: '#ffffff',
          },
          Button: {
            controlHeight: 36,
          },
        },
      }}
    >
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<PrivateRoute />}>
          <Route element={<AdminLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route
              path="/leads"
              element={
                <RoleRoute roles={['super_admin', 'admin', 'operator', 'leasing_director', 'leasing_manager', 'rental_consultant']}>
                  <LeadsPage />
                </RoleRoute>
              }
            />
            <Route
              path="/rewards"
              element={
                // ST3（2026-10-20）：放开 leasing_manager（经 REWARD_OPERATE 查看/核算/导出）
                <RoleRoute roles={['super_admin', 'admin', 'finance', 'finance_manager', 'leasing_manager']}>
                  <RewardsPage />
                </RoleRoute>
              }
            />
            <Route
              path="/payments"
              element={
                // ST3（2026-10-20）：放开 leasing_manager（经 REWARD_OPERATE 查看收款）
                <RoleRoute roles={['super_admin', 'admin', 'finance', 'finance_manager', 'leasing_manager']}>
                  <PaymentsPage />
                </RoleRoute>
              }
            />
            <Route
              path="/blacklist"
              element={
                // ST3（2026-10-20）：对齐后端 BLACKLIST_VIEW = SA/A/O/LM/RC，放开 leasing_manager + rental_consultant
                <RoleRoute roles={['super_admin', 'admin', 'operator', 'leasing_manager', 'rental_consultant']}>
                  <BlacklistPage />
                </RoleRoute>
              }
            />
            <Route
              path="/review-center"
              element={
                <RoleRoute roles={['super_admin', 'admin', 'leasing_manager']}>
                  <ReviewCenterPage />
                </RoleRoute>
              }
            />
            <Route
              path="/referrers"
              element={
                <RoleRoute roles={['super_admin', 'admin', 'operator', 'finance', 'finance_manager', 'leasing_director', 'leasing_manager', 'rental_consultant']}>
                  <ReferrersPage />
                </RoleRoute>
              }
            />
            <Route
              path="/risk-control"
              element={
                <RoleRoute roles={['super_admin', 'admin']}>
                  <RiskControlPage />
                </RoleRoute>
              }
            />
            <Route
              path="/system-config"
              element={
                // account_self_v1（2026-09-23）：全 8 角色可进入；页内按角色/权限过滤 tab（见 SystemConfig/index.tsx）
                <RoleRoute roles={['super_admin', 'admin', 'operator', 'finance', 'finance_manager', 'leasing_director', 'leasing_manager', 'rental_consultant']}>
                  <SystemConfigPage />
                </RoleRoute>
              }
            />
            <Route
              path="/project-intro"
              element={
                <RoleRoute roles={['super_admin', 'admin', 'operator', 'leasing_director', 'leasing_manager', 'rental_consultant']}>
                  <ProjectIntroPage />
                </RoleRoute>
              }
            />
            {/* thankyou_v2b（批次 8.2）：「被推荐人礼遇」已并入「答谢方案」（/benefit-config）。
                旧路由改为重定向，照 /projects → /project-intro 先例，避免旧书签 404。 */}
            <Route path="/referred-benefits" element={<Navigate to="/benefit-config" replace />} />
            {/* thankyou_v1（批次 2）：答谢配置（类型/业态/方案三 tab），仅超管/管理员，照 /risk-control 样板 */}
            <Route
              path="/benefit-config"
              element={
                <RoleRoute roles={['super_admin', 'admin']}>
                  <BenefitConfigPage />
                </RoleRoute>
              }
            />
            {/* thankyou_v2b（批次 8.2）：商户档案，仅超管/管理员（照 /benefit-config 先例，
                后端写操作挂 MERCHANT_MANAGE = ['super_admin','admin']） */}
            <Route
              path="/merchants"
              element={
                <RoleRoute roles={['super_admin', 'admin']}>
                  <MerchantsPage />
                </RoleRoute>
              }
            />
            {/* merge_projects_into_projectintro_v1：「项目管理」已并入「项目介绍」页标签页。
                保留 /projects 作重定向，避免旧书签/收藏链接 404。 */}
            <Route path="/projects" element={<Navigate to="/project-intro" replace />} />
            <Route
              path="/reports"
              element={
                <RoleRoute roles={['super_admin', 'admin', 'finance', 'finance_manager', 'leasing_director', 'leasing_manager']}>
                  <ReportsPage />
                </RoleRoute>
              }
            />
          </Route>
        </Route>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <style>{MOBILE_COMPAT_CSS}</style>
    </ConfigProvider>
  )
}

export default App
